/**
 * DEF-1 — Definitions wire schemas (Definitions service, /api/v1/definitions).
 * Tenant reference-data code lists: groups + values, both self-nesting. Mirrors
 * DefinitionGroupDto / DefinitionDto field-by-field; Zod-parsed at the seam.
 */
import { z } from 'zod';

import { pagedListSchema } from './education';

const isoDate = z.string().min(1);

/** A catalog of code-list values (e.g. event_types, sponsor_tiers). Self-nests via parentId. */
export const definitionGroupSchema = z.object({
  id: z.guid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  orderNo: z.number(),
  isSystem: z.boolean(),
  isActive: z.boolean(),
  parentId: z.guid().nullish(),
  icon: z.string().nullish(),
  createdAt: isoDate,
  updatedAt: isoDate.nullish()
});
export const definitionGroupListSchema = pagedListSchema(definitionGroupSchema);

/** A value inside a group. Consumers store `code` opaquely. Self-nests via parentId. */
export const definitionSchema = z.object({
  id: z.guid(),
  groupId: z.guid(),
  groupKey: z.string(),
  code: z.string(),
  name: z.string(),
  orderNo: z.number(),
  isDefault: z.boolean(),
  color: z.string().nullish(),
  icon: z.string().nullish(),
  parentId: z.guid().nullish(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  createdAt: isoDate,
  updatedAt: isoDate.nullish()
});
export const definitionListSchema = pagedListSchema(definitionSchema);

export type DefinitionGroup = z.infer<typeof definitionGroupSchema>;
export type Definition = z.infer<typeof definitionSchema>;

/**
 * AGECAT-PT (mig 471) — an age band belongs to a PROGRAM TYPE. `programTypeId` is an opaque ref into
 * the `program_types` definition group; consumers store the band's own `id` opaquely too.
 *
 * ⚠ THE `type` FIELD ('math' | 'english') IS GONE, and with it `ageCategoryTypeSchema`. It was a
 * closed two-value enum standing in for "the two programmes the tenant runs" — which stopped being
 * true the moment program types became tenant-editable. Anything that still needs to group or filter
 * bands does it by `programTypeId` and resolves the LABEL from the definitions group, never from a
 * hardcoded pair.
 */
export const ageCategorySchema = z.object({
  id: z.guid(),
  name: z.string(),
  minAge: z.number(),
  maxAge: z.number(),
  programTypeId: z.guid(),
  orderNo: z.number(),
  isActive: z.boolean(),
  createdAt: isoDate,
  updatedAt: isoDate.nullish()
});
export const ageCategoryListSchema = pagedListSchema(ageCategorySchema);

export type AgeCategory = z.infer<typeof ageCategorySchema>;

/**
 * POST/PUT /api/v1/definitions/age-categories body. `programTypeId` is REQUIRED — a band with no
 * programme has nowhere to be shown, and the server answers 422 when the id is not a live, active
 * value of the `program_types` group.
 */
export interface AgeCategoryRequest {
  name: string;
  minAge: number;
  maxAge: number;
  programTypeId: string;
  orderNo?: number;
  isActive?: boolean;
}

/** POST/PUT /api/v1/definitions/groups body. */
export interface DefinitionGroupRequest {
  key: string;
  name: string;
  description?: string | null;
  orderNo?: number;
  isActive?: boolean;
  parentId?: string | null;
  icon?: string | null;
}

/** POST/PUT /api/v1/definitions/items body. */
export interface DefinitionRequest {
  groupKey: string;
  code: string;
  name: string;
  orderNo?: number;
  isDefault?: boolean;
  color?: string | null;
  icon?: string | null;
  parentId?: string | null;
  isActive?: boolean;
}
